import { T, useT } from '@transifex/react';
import { HStack, Text, VStack } from '@vidahealth/vida-ui';
import { useEffect } from 'react';
import { Image, View } from 'react-native';

import { trackEvent } from '@/modules/analytics';
import { useRulesEngine } from '@/modules/rulesEngine';
import { CtaButton, Screen, ScreenHeading, VidaLogo } from '@/modules/ui';

const getOrgInitials = (name?: string): string => {
  if (!name) return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
};

export function EligibleScreen() {
  const t = useT();
  const { draft, user, selectedOrg, setEligibilityCheckResult } = useRulesEngine();

  // /eligible is a transient celebration the driver resolves to right after a
  // passing check (eligibilityCheckResult.passed === true — runEligibilityCheck
  // already refreshed the user, so payingOrganization + its image are in state).
  // Continue clears that pass signal so the driver advances to /create-profile
  // (or the survey). It's never a resting state: on a refresh the in-memory
  // result is gone, so the driver routes by durable state and the layout
  // redirects away.
  const onContinue = () => {
    // Sparta's "eligibility-success-click-continue". Identifiers only — no PII.
    trackEvent('eligibility-success-click-continue', {
      uuid: user?.uuid,
      payingOrgUuid: selectedOrg?.uuid,
    });
    setEligibilityCheckResult(undefined);
  };

  // Prefer the refreshed payingOrganization (carries the org image); fall back
  // to the draft / selected partner if a refresh hasn't landed.
  const payingOrg = user?.payingOrganization;
  const firstName = draft.firstName ?? user?.firstName;
  const orgName = payingOrg?.name || draft.orgName || selectedOrg?.name;
  const orgInitials = getOrgInitials(orgName);
  const orgImageUrl = payingOrg?.image?.url || selectedOrg?.imageUrl;

  // Sparta fires "eligible" on the success screen load with the user + paying
  // org urn. Keyed on the identifiers so it fires once they've resolved (the
  // user was refreshed on the passing check before this screen renders).
  useEffect(() => {
    if (user?.uuid) {
      trackEvent('eligible', { uuid: user.uuid, payingOrgUrn: payingOrg?.urn });
    }
  }, [user?.uuid, payingOrg?.urn]);

  return (
    <Screen
      contentStyle={{ justifyContent: 'center' }}
      footer={
        <CtaButton onPress={onContinue} testID='eligible-continue'>
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='4xl'>
        <View className='rounded-lg border border-outline-100 bg-background-50 px-6 py-8 dark:border-outline-300 dark:bg-background-700'>
          <HStack className='items-center justify-center gap-5'>
            <View className='h-16 flex-1 items-center justify-center'>
              {orgImageUrl ? (
                <Image source={{ uri: orgImageUrl }} resizeMode='contain' className='h-16 w-full' />
              ) : (
                <View className='h-16 min-w-16 items-center justify-center rounded-lg bg-primary-50 px-3 dark:bg-primary-900'>
                  <Text className='text-center font-heading-semibold text-primary-800 dark:text-primary-0'>
                    {orgInitials || t('Org')}
                  </Text>
                </View>
              )}
            </View>

            <Text size='4xl' className='font-body-thin text-typography-900 dark:text-typography-0'>
              +
            </Text>

            <View className='h-16 flex-1 items-center justify-center'>
              <VidaLogo width={110} height={29} mode='light' />
            </View>
          </HStack>
        </View>

        <VStack space='md'>
          <ScreenHeading
            title={
              firstName
                ? t("Hooray {firstName}! You're connected", { firstName })
                : t("You're connected")
            }
            center
          />
          {orgName ? (
            <Text size='lg' className='text-center font-body-semibold text-typography-900'>
              <T _str='Brought to you at no charge by {payingOrgName}.' payingOrgName={orgName} />
            </Text>
          ) : null}
          <Text className='text-center text-typography-700'>
            <T _str="A personalized health program especially for you, compliments of your organization. Let's get you started to a healthier you!" />
          </Text>
        </VStack>
      </VStack>
    </Screen>
  );
}
